import { useCallback, useEffect, useState } from 'react';
import type { ReportDetail, ReportListResponse, TaskStatus } from '../types';
import { reportsService } from '../services/reports';

export function useReports(reportId?: string) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<TaskStatus | ''>('');
  const [list, setList] = useState<ReportListResponse | null>(null);
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [listError, setListError] = useState('');
  const [detailError, setDetailError] = useState('');
  const [retryingArticleId, setRetryingArticleId] = useState('');
  const loadList = useCallback(
    async (signal?: AbortSignal) => {
      setListLoading(true);
      setListError('');
      try {
        setList(await reportsService.list({ page, pageSize: 20, status }, signal));
      } catch (e) {
        if (!(e instanceof DOMException && e.name === 'AbortError'))
          setListError(e instanceof Error ? e.message : '报告列表加载失败');
      } finally {
        setListLoading(false);
      }
    },
    [page, status]
  );
  const loadDetail = useCallback(
    async (signal?: AbortSignal) => {
      if (!reportId) {
        setDetail(null);
        return;
      }
      setDetailLoading(true);
      setDetailError('');
      try {
        setDetail(await reportsService.detail(reportId, signal));
      } catch (e) {
        if (!(e instanceof DOMException && e.name === 'AbortError'))
          setDetailError(e instanceof Error ? e.message : '报告详情加载失败');
      } finally {
        setDetailLoading(false);
      }
    },
    [reportId]
  );
  useEffect(() => {
    const c = new AbortController();
    void loadList(c.signal);
    return () => c.abort();
  }, [loadList]);
  useEffect(() => {
    const c = new AbortController();
    void loadDetail(c.signal);
    return () => c.abort();
  }, [loadDetail]);
  const retry = useCallback(
    async (articleId: string) => {
      if (!reportId) return;
      setRetryingArticleId(articleId);
      try {
        await reportsService.retry(reportId, articleId);
        await loadDetail();
        await loadList();
      } finally {
        setRetryingArticleId('');
      }
    },
    [reportId, loadDetail, loadList]
  );
  return {
    page,
    setPage,
    status,
    setStatus,
    list,
    detail,
    listLoading,
    detailLoading,
    listError,
    detailError,
    reloadList: loadList,
    reloadDetail: loadDetail,
    retry,
    retryingArticleId
  };
}
